import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, AlertTriangle, Users, Calendar, RefreshCw, 
  ChevronDown, ChevronUp, Loader2, Target, Lightbulb
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function Analytics() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [forecastDays, setForecastDays] = useState(7);

  useEffect(() => {
    fetchPredictions();
  }, [forecastDays]);

  const fetchPredictions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/analytics/predictions?days=${forecastDays}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch(level) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-amber-600 bg-amber-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  const getRiskLabel = (level) => {
    switch(level) {
      case 'high': return t('analytics.highRisk');
      case 'medium': return t('analytics.mediumRisk');
      default: return t('analytics.lowRisk');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-primary" />
            {t('analytics.title')}
          </h1>
          <p className="text-slate-600">{t('analytics.absencePrediction')}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={forecastDays}
            onChange={(e) => setForecastDays(Number(e.target.value))}
            className="px-3 py-2 border rounded-md text-sm"
          >
            <option value={7}>{t('analytics.nextWeek')}</option>
            <option value={14}>2 semaines</option>
            <option value={30}>{t('analytics.nextMonth')}</option>
          </select>
          <Button variant="outline" onClick={fetchPredictions} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total analysé</p>
                  <p className="text-2xl font-bold">{data.summary.total_employees}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-red-600">{t('analytics.highRisk')}</p>
                  <p className="text-2xl font-bold text-red-700">{data.summary.high_risk}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Target className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-amber-600">{t('analytics.mediumRisk')}</p>
                  <p className="text-2xl font-bold text-amber-700">{data.summary.medium_risk}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Calendar className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-green-600">{t('analytics.lowRisk')}</p>
                  <p className="text-2xl font-bold text-green-700">{data.summary.low_risk}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Risk Predictions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.absencePrediction')}</CardTitle>
          <CardDescription>
            Analyse basée sur les 90 derniers jours de présence
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data?.predictions?.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Aucune donnée à analyser</p>
          ) : (
            <div className="space-y-3">
              {data?.predictions?.map((pred) => (
                <div
                  key={pred.employee_id}
                  className="border rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpandedEmployee(
                      expandedEmployee === pred.employee_id ? null : pred.employee_id
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(pred.risk_level)}`}>
                        {pred.risk_score}%
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{pred.employee_name}</p>
                        <p className="text-sm text-slate-500">{pred.position}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${getRiskColor(pred.risk_level).split(' ')[0]}`}>
                        {getRiskLabel(pred.risk_level)}
                      </span>
                      {expandedEmployee === pred.employee_id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedEmployee === pred.employee_id && (
                    <div className="px-4 pb-4 border-t bg-slate-50">
                      {/* Patterns */}
                      {pred.patterns?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-slate-700 mb-2">
                            {t('analytics.patterns')}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {pred.patterns.map((pattern, i) => (
                              <span key={i} className="px-2 py-1 bg-slate-200 rounded text-xs text-slate-700">
                                {pattern}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Predictions */}
                      {pred.predicted_absences?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-slate-700 mb-2">
                            {t('analytics.forecast')}
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                            {pred.predicted_absences.slice(0, 7).map((day, i) => (
                              <div
                                key={i}
                                className={`p-2 rounded text-center text-xs ${
                                  day.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                                  day.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-green-100 text-green-700'
                                }`}
                              >
                                <p className="font-medium">{day.date.slice(5)}</p>
                                <p>{day.absence_probability}%</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Recommendations */}
                      {pred.recommendations?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                            <Lightbulb className="w-4 h-4 text-amber-500" />
                            {t('analytics.recommendations')}
                          </p>
                          <ul className="text-sm text-slate-600 space-y-1">
                            {pred.recommendations.map((rec, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-primary">•</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Comment fonctionne l'analyse prédictive ?</p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Analyse des patterns d'absence sur les 90 derniers jours</li>
                <li>Détection des tendances (lundi/vendredi, fins de mois)</li>
                <li>Score de risque basé sur le taux d'absence et retards</li>
                <li>Recommandations RH personnalisées</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
